import React, { useEffect, useState } from "react";
import { CustomCard } from "../CustomCard";
import moment from "moment";
import clsx from "clsx";
import { Box, Divider, Typography } from "@material-ui/core";
import { getDownloadableLink } from "../../../Helper";
import { GetFileIconbyDocumentName } from "../DocumentsContactCard";
import { useIsAr, useSelectedTheme, useTranslate } from "../../../Hooks";
import { CustomMenu } from "../CustomMenu";
import { DeleteConfirmationDialog } from "../DeleteConfirmationDialog";
import { DeleteScopeDocument } from "../../../Services";
import { useVerticalNav } from "../../../Contexts/VerticalNavContext";
import {
  PropertyShareDocumentsDialog,
  PropertyUploadDocument,
} from "../../../Views/Home/NewPropertiesView/PropertyOverview/UI/DocumentTab/UI";


// styles
import useStyle from "./styles";

// icons
import {
  AttatchmentIcon,
  DeleteIcon,
  DownloadIcon,
  EditIcon,
  ErrorImageIcon,
  EyeIcon,
  FileIcon,
  Image01Icon,
  MoreVertIcon,
  ShareIcon,
} from "../../../assets/icons";

const DocumentsPropertyCard = ({ documentItem, refetchData, propertyId }) => {
  const styles = useStyle();
  const {
    theme: { palette },
  } = useSelectedTheme();
  const { isAr } = useIsAr();
  const { setAlertBoxContent } = useVerticalNav();
  const { translate } = useTranslate("NewPropertiesView");
  const [imageUrl, setImageUrl] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [isDeleteDocConfirmationOpen, setIsDeleteDocConfirmationOpen] =
    useState(false);
  const [isShareDocOpen, setIsShareDocOpen] = useState(false);
  const [isUpdateDocDialogIsOpen, setIsUpdateDocDialogIsOpen] = useState(false);
  function formatDocumentName(documentName) {
    if (!documentName) return "Untitled";
    const namePart = documentName.split(".")[0].slice(0, 13);
    const extension = documentName.split(".")[1];
    return `${namePart}${extension ? "." + extension : ""}`;
  }

  const formattedDate = documentItem?.createdOn
    ? moment(documentItem?.createdOn).locale("en").format("D MMM YYYY")
    : "Date Unknown";

  function formatCreatorName(creator, isAr) {
    return creator
      ? isAr
        ? `${creator.slice(-9)}...`
        : `${creator.slice(0, 9)}...`
      : "Unknown";
  }

  const documentName = formatDocumentName(documentItem?.documentName);
  const creatorName = formatCreatorName(documentItem?.createdBy, isAr);

  const DeleteDocHandler = async (scopeDocumentId) => {
    try {
      const res = await DeleteScopeDocument(scopeDocumentId);
      if (!(res && res.status && res.status !== 200)) {
        setAlertBoxContent({
          display: true,
          variant: "success",
          title: translate("document-deleted-successfully"),
          onClose: () => {
            setAlertBoxContent(null);
          },
        });
        refetchData();
      } else {
        setAlertBoxContent({
          display: true,
          variant: "error",
          title: translate("Something-went-wrong"),
          onClose: () => {
            setAlertBoxContent(null);
          },
        });
      }
    } catch (error) {
      console.error("sorry-something-wrong-please-try-again!", error);
    } finally {
    }
  };

  const DownloadDocHandler = (item) => {
    if (!item) {
      return;
    }
    try {
      const link = document.createElement("a");
      link.setAttribute("download", item.documentName);
      link.href = getDownloadableLink(item.documentId);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Download error:", error);
    }
  };

  useEffect(() => {
    // only for imagetype
    if (
      documentItem.documentId &&
      ["png", "jpg", "jpeg", "gif", "bmp", "tiff", "webp", "svg"].includes(
        documentName.split(".").pop().toLowerCase()
      )
    ) {
      setImageUrl(getDownloadableLink(documentItem.documentId));
    }
  }, [documentItem.documentId]);

  return (
    <>
      <CustomCard
        boxShadow="xs"
        borderRadius="xl"
        borderColor="secondary"
        classes={styles.cardWrapper}
      >
        <Box className={styles.cardContainer}>
          <Box
            className={clsx(styles.headingContainer, {
              [styles.withUrl]: documentItem?.documentLink,
              [styles.withImage]:
                documentItem.documentId &&
                [
                  "png",
                  "jpg",
                  "jpeg",
                  "gif",
                  "bmp",
                  "tiff",
                  "webp",
                  "svg",
                ].includes(documentName.split(".").pop().toLowerCase()),
              [styles.withDocument]:
                documentName &&
                ![
                  "png",
                  "jpg",
                  "jpeg",
                  "gif",
                  "bmp",
                  "tiff",
                  "webp",
                  "svg",
                ].includes(documentName.split(".").pop().toLowerCase()),
            })}
          >
            <Box className={styles.imageWrapper}>
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={documentItem.documentName}
                  className={styles.imagePreview}
                  onError={(e) => {
                    e.target.onerror = null;
                    setImageError(true);
                  }}
                />
              ) : imageError ? (
                <ErrorImageIcon
                  width="28"
                  height="28"
                  fill={palette.border.tertiary}
                  className={styles.errorIcon}
                />
              ) : documentItem?.documentLink ? (
                <Box className={styles.containerAttatchmentIcon}>
                  <AttatchmentIcon
                    width="28"
                    height="28"
                    fill={palette.border.tertiary}
                  />
                </Box>
              ) : documentName ? (
                <>
                  {GetFileIconbyDocumentName({
                    documentName: documentName
                      ?.split(".")
                      ?.pop()
                      ?.toLowerCase(),
                    height: "56",
                    width: "56",
                  })}
                </>
              ) : (
                <span className={styles.noImageText}>
                  There is no preview available
                </span>
              )}
            </Box>
            <CustomMenu
              menuItems={[
                documentItem.documentId && {
                  text: "Preview",
                  icon: (
                    <EyeIcon
                      width="16"
                      height="16"
                      fill={palette.foreground.quarterary}
                    />
                  ),
                  handler: () => {},
                },
                {
                  text: "Edit",
                  icon: (
                    <EditIcon
                      width="16"
                      height="16"
                      fill={palette.foreground.quarterary}
                    />
                  ),
                  handler: () => {
                    setIsUpdateDocDialogIsOpen(true)
                  },
                },
                {
                  text: "Share",
                  icon: (
                    <ShareIcon
                      width="16"
                      height="16"
                      fill={palette.button.secondaryGray_fg}
                    />
                  ),
                  handler: () => {
                    setIsShareDocOpen(true);
                  },
                },
                documentItem.documentId && {
                  text: "Download",
                  icon: (
                    <DownloadIcon
                      width="16"
                      height="16"
                      fill={palette.button.secondaryGray_fg}
                    />
                  ),
                  handler: () => {
                    DownloadDocHandler(documentItem);
                  },
                },
                {
                  text: "Delete",
                  icon: (
                    <DeleteIcon
                      width="16"
                      height="16"
                      fill={palette.button.secondaryGray_fg}
                    />
                  ),
                  handler: () => {
                    setIsDeleteDocConfirmationOpen(true);
                  },
                },
              ].filter(Boolean)}
              activeIconClass={styles.menuActiveIconClass}
              defaultIconClass={styles.menuDefaultIconClass}
            >
              <MoreVertIcon width="20" height="20" />
            </CustomMenu>
          </Box>

          <Box className={styles.contentContainer}>
            <Box className={styles.titleWrapper}>
              <Typography
                className={styles.categoryName}
                variant="h6"
                align="center"
              >
                {documentItem?.categoryName || "No Category"}
              </Typography>
              <Box className={styles.documentNameWrapper}>
                {documentItem.documentName ? (
                  [
                    "png",
                    "jpg",
                    "jpeg",
                    "gif",
                    "bmp",
                    "tiff",
                    "webp",
                    "svg",
                  ].includes(documentName.split(".").pop().toLowerCase()) ? (
                    <>
                      <Image01Icon
                        width="20"
                        height="20"
                        fill={palette.utility.utility_gray_400}
                      />
                      <span className={styles.documentName}>
                        {documentName}
                      </span>
                    </>
                  ) : (
                    <>
                      <FileIcon
                        width="20"
                        height="20"
                        fill={palette.utility.utility_gray_400}
                      />
                      <span className={styles.documentLink}>
                        {documentItem.documentName}
                      </span>
                    </>
                  )
                ) : documentItem?.documentLink ? (
                  <>
                    <AttatchmentIcon
                      width="20"
                      height="20"
                      fill={palette.utility.utility_gray_400}
                    />
                    <span className={styles.documentLink}>
                      {documentItem.documentLink}
                    </span>
                  </>
                ) : null}
              </Box>
            </Box>
            <Divider />
            <Box className={styles.footerContainer}>
              <Typography
                className={styles.createdOn}
                variant="body1"
                align="center"
              >
                Created On: <b className={styles.date}>{formattedDate}</b>{" "}
                <span>{creatorName}</span>
              </Typography>
            </Box>
          </Box>
        </Box>
      </CustomCard>
      {isDeleteDocConfirmationOpen && (
        <DeleteConfirmationDialog
          onClose={() => {
            setIsDeleteDocConfirmationOpen(false);
          }}
          title={translate("Your-document-will-be-deleted")}
          subTitle={translate(
            "Are-you-sure-you-want-to-delete-this-document-This-action-cannot-be-undone"
          )}
          onConfirm={() => {
            DeleteDocHandler(documentItem.scopeDocumentId);
            setIsDeleteDocConfirmationOpen(false);
          }}
          confirmText={translate("Delete")}
        />
      )}
      {isShareDocOpen && (
        <PropertyShareDocumentsDialog
          open={isShareDocOpen}
          onClose={() => {
            setIsShareDocOpen(false);
          }}
          document={documentItem}
        />
      )}

      {isUpdateDocDialogIsOpen && (
        <PropertyUploadDocument
          open={isUpdateDocDialogIsOpen}
          close={() => {
            setIsUpdateDocDialogIsOpen(false);
          }}
          propertyId={propertyId}
          refetchData={()=>{
            refetchData()
          }}
          activeItem={documentItem}
        />
      )}
    </>
  );
};

export default DocumentsPropertyCard;
